import React, {createRef, useState, useContext} from 'react';
import { DragDropContext, Droppable, Draggable } from '@react-forked/dnd';
import axios from "axios";
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import __ from "lodash";
import MaterialTable from "@material-table/core";
import {defaultComponentsScroll, MaterialTableBody, MaterialTableBodyRow} from "../../../controls/Material-Table-Components"
import {TablePagination, Button, Chip, useTheme} from "@material-ui/core";
import { ComboSelect, ShcSelect, MaskedNumber } from "../../../controls";
import SystemContext from "../../../SystemContext";
import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import TariffLinesValidator from "../validators/TariffLinesValidator";
import AirportLookup from "../../Airports/components/AirportsLookup";
import TariffLineBands from "./TariffBandList";
import { useTariffLines, useAddTariffLine, useUpdateTariffLine, useDeleteTariffLine } from "../../../services/tariff";
import { getApiSitePrefix } from "../../../functions/SiteDefaults";
import useDimensions from '../../../blocks/useDimensions';

const apiSitePrefix = getApiSitePrefix();
const detailPanelIcons = ["monetization_on", "euro", "currency_pound"];
const detailPanelIconsOpen = ["attach_money", "euro", "currency_pound"];


const TariffLineList = (_props) => {
  const {isAuthenticated} = useAuth0();
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const eventBus = SystemData.eventBus;
  const validatorColumn = ValidatorColumn(TariffLinesValidator, true);

  const props = {
    ..._props
  }
  const [state, setState] = useState({
    canEdit: props.canEdit,
    activeRow: null,
    tariff: {}
  });
  const classes = props.classes;
  if (eventBus) {
    eventBus.bind('TariffHeaderList.rowSelected', (tariff)=>{
      setState({...state, tariff});
    });
  }

  const tariff = state.tariff;

  const tableRef = createRef({state: {lastEditingRow: false}});

  const handleAddTariffLine = useAddTariffLine();
  const handleUpdateTariffLine = useUpdateTariffLine();
  const handleDeleteTariffLine = useDeleteTariffLine();

  const queryClient = useQueryClient();
  const extraKeys = [tariff.tariffHeaderUuid];

  const tariffLines = useTariffLines({tableRef, queryClient, extraKeys});

  const updatePriorities = async (changedRecords) => {
    SystemData.toggleLoadingProgress(true);
    let response = null;
    try {
      response = await axios.put(`${apiSitePrefix}/tarifflines/reorder`, changedRecords);
    } catch (err) {
      SystemData.openAlert(
        "Error",
        `An unexpected error has occurred re-ordering the priorities.`
      );
      console.debug(err);
    }
    if (!response) {
      SystemData.openAlert(
        "Error",
        `An unexpected error has occurred re-ordering the priorities.`
      );
    } else if (response.error) {
      SystemData.openAlert(
        "Error",
        `Error: ${response.error.errorMessage}`
      );
    }
    queryClient.invalidateQueries(["TariffLines"]);
    SystemData.toggleLoadingProgress(false);
  }

  const onDragEnd = (dragProps) => {
    if (!dragProps) return;
    if (!dragProps.source || !dragProps.destination) return;
    if (dragProps.source.index === dragProps.source.destination) return;
    if (bodyRef.current && bodyRef.current.props && bodyRef.current.props.renderData) {
      let records = [...bodyRef.current.props.renderData];
      let sourceRecord = records.splice(dragProps.source.index,1);
      const part1 = [...records].slice(0,dragProps.destination.index);
      const part2 = [...records].slice(dragProps.destination.index);
      const newRecords = [...part1, ...sourceRecord, ...part2];
      const associatedRecords = __.filter(newRecords, __.pick(sourceRecord[0], ['origin', 'destination', 'chargeCode']));
      const changes = [];
      for (let i = 0; i < associatedRecords.length; i++) {
        const newPriority = ((i + 1) * 10);
        if (associatedRecords[i].priority !== newPriority) {
          changes.push({...associatedRecords[i], priority: newPriority});
        }
      }
      const key = ["TariffLines", tariff.tariffHeaderUuid];
      const queryData = queryClient.getQueryData(key);
      for (let i = 0; i < changes.length; i++) {
        const {tariffLineUuid, priority} = changes[i];
        const queryLine = __.find(queryData, {tariffLineUuid});
        if (queryLine) {
          queryLine['priority'] = priority;
        }
      }
      const sortedQueryData = __.sortBy(queryData, ['origin', 'destination', 'chargeCode', 'priority']);
      queryClient.setQueryData(key, sortedQueryData);

      updatePriorities(changes);
    }
  };

  const getColumns = (props) => {
    const baseColumn = {
      cellStyle: { fontSize:14, padding: "8px 2px" },
      headerStyle: { padding: "8px 0px" },
      filterCellStyle: { padding: "8px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
      sorting: false,
    };
    // const formatChars={
    //   "U": "[A-Z]",
    //   "A": "[A-Z0-9]",
    //   "a": "[A-Za-z0-9]",
    //   "i": "[A-Za-z0-9\\-]",
    //   "9": "[0-9]",
    //   "D": "[0-9.]",
    //   "-": "-",
    //   "x": "."
    // }
    // const defaultEditProps = {
    //   style:{fontSize: "0.875rem"}
    // };

    return [
      {...validatorColumn},
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */

      {
        ...baseColumn,
        ...ColumnWidth(80, undefined,undefined,undefined, {width: 76},undefined,false),
        title: "Origin",
        field: "origin",
        initialEditValue: "",
        editable: state.canEdit ? "onAdd" : "never",
        editComponent: (props) => (
          <AirportLookup
            value={props.value}
            onChange={props.onChange}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(100, undefined,undefined,undefined, undefined,undefined,false),
        title: "Destination",
        field: "destination",
        editable: state.canEdit ? "onAdd" : "never",
        initialEditValue: "",
        editComponent: (props) => (
          <AirportLookup
            value={props.value}
            onChange={props.onChange}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(130, undefined,undefined,undefined, undefined,undefined,false),
        title: "Charge Code",
        field: "chargeCode",
        editable: state.canEdit ? "onAdd" : "never",
        initialEditValue: "",
        render: (rowData) => (
          <ComboSelect
            id="chargeCode"
            group="TARIFF CHARGE CODES"
            subGroup=""
            fullWidth
            value={rowData.chargeCode}
            displayOnly={true}
          />
        ),
        editComponent: (props) => (
          <ComboSelect
            id="chargeCode"
            group="TARIFF CHARGE CODES"
            subGroup=""
            fullWidth
            value={props.value}
            onChange={props.onChange}
          />
        )
      },

      {
        ...baseColumn,
        ...ColumnWidth(100, undefined,undefined,undefined, undefined,undefined,false),
        title: "Deck",
        field: "deck",
        initialEditValue: "",
        render: (rowData) => (
          <ComboSelect
            id="deck"
            group="DECKS"
            subGroup=""
            fullWidth
            value={rowData.deck}
            displayOnly={true}
          />
        ),
        editComponent: (props) => (
          <ComboSelect
            id="deck"
            group="DECKS"
            subGroup=""
            fullWidth
            value={props.value}
            onChange={props.onChange}
          />
        )

      },
      {
        ...baseColumn,
        title: "Shcs",
        field: "shcs",
        initialEditValue: "",
        editComponent: (props) => (
          <ShcSelect
            values={props.value||[]}
            fullWidth
            autoMarkDgr={false}
            style={{height: 29}}
            chipsStyle={{height:29, overflow: "auto"}}
            onChange={(values)=>{props.onChange(values)}}
          />
        ),
        render: (rowData) => (
          <div className={classes.chips}>
            {(rowData.shcs||[]).map((value) => (
              <Chip key={value} label={value} className={classes.chip} size={"small"}/>
            ))}
          </div>
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(80, undefined,undefined,undefined, undefined,undefined,false),
        title: "Shcs Required",
        field: "shcsRequired",
        initialEditValue: "",
        render: (rowData) => (
          <ComboSelect
            id="shcsRequired"
            group="SHCS REQUIRED"
            subGroup=""
            fullWidth
            value={rowData.shcsRequired}
            displayOnly={true}
          />
        ),
        editComponent: (props) => (
          <ComboSelect
            id="shcsRequired"
            group="SHCS REQUIRED"
            subGroup=""
            fullWidth
            value={props.value}
            onChange={props.onChange}
          />
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(120, undefined,undefined,undefined, undefined,undefined,false),
        title: "Minimum Charge",
        field: "minimumCharge",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,
        editComponent: (props) => (
          <MaskedNumber
            value={props.value}
            decimalScale={2}
            fixedDecimalScale={true}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.minimumCharge}
            decimalScale={2}
            fixedDecimalScale={true}
          />
        )
      },
    ];
  };

  // eslint-disable-next-line no-unused-vars
  const [tableContainerRef, { width: tableWidth, height: tableHeight}] = useDimensions({});

  const theader = tableContainerRef.current?.querySelector('thead');
  const thead = theader ? theader.clientHeight:36;
  const bodyHeight = (tableHeight - thead) - 14 ;

  const bodyRef = createRef();

  return (isAuthenticated &&
    <div style={{width: "100%", marginLeft: 20, marginRight: 20}}>
      <div ref={tableContainerRef} className={state.canEdit?classes.tariffLineListEdit:classes.tariffLineList} style={{width: "100%", minWidth: "100%", height: `calc(100vh - ${props.height + 82}px)`}}>
      {!state.tariff || Object.keys(state.tariff).length === 0?(
        <div style={{position: "relative", width: "100%", top: "50%", height: "30px", textAlign: "center"}}>
          <Button color={"primary"}>Select a Tariff to view additional details</Button>
        </div>
      ):(
        <MaterialTable
          tableRef={tableRef}
          style={{
            border: "none",
            boxShadow: "none",
            borderRadius: 0,
          }}
          options={{
            actionsColumnIndex: -1,
            cellStyle: {
              padding: "8px 2px",
            },
            headerRowStyle: {
              display: "table",
              width: "100%",
            },
            headerStyle: {
              backgroundColor: theme.palette.tableHeader.backgroundColor,
              color: theme.palette.tableHeader.textColor,
              padding: "2px",
            },
            draggable: false,
            padding: "dense",
            toolbarButtonAlignment: "right",
            pageSize: 10,
            detailPanelType: "single",
            paging: false,
          }}
          components={{
            ...defaultComponentsScroll,
            Body: (props) => (
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable
                  droppableId={"singleColumnDroppableAreaThusStaticInput"}
                >
                  {(provided) => (
                    <MaterialTableBody
                      ref={bodyRef}
                      {...props}
                      forwardedRef={provided.innerRef}
                      placeHolder={provided.placeholder}
                      rowHeight={40}
                      overscanRowCount={1000}
                      addVirtualScrolling={true}
                      bodyHeight={bodyHeight}
                      headerHeight={thead}
                      filterRowHeight={40}
                    />
                  )}
                </Droppable>
              </DragDropContext>
            ),
            Row: (props) => (
              <Draggable
                draggableId={props.data.tableData.id.toString()}
                index={props.data.tableData.id}
              >
                {(provided) => {
                  return (
                    <MaterialTableBodyRow
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      {...props}
                      forwardedRef={provided.innerRef}
                    />
                  );
                }}
              </Draggable>
            ),
            Pagination: (props) => (
              <TablePagination
                {...props}
                rowsPerPageOptions={[5, 10, 15]}
              />
            ),
          }}
          detailPanel={
            [
              {
                tooltip: "Price Bands",
                icon: `${detailPanelIcons[["USD","EUR","GBP"].indexOf(tariff.currencyCode || tariff.defaultCurrencyCode)]}`,
                openIcon: `${detailPanelIconsOpen[["USD","EUR","GBP"].indexOf(tariff.currencyCode || tariff.defaultCurrencyCode)]}`,
                render: (rowData) => {
                  return (
                    <div
                      className={classes.tariffBandList}
                      style={{ backgroundColor: "#f2f2f2"}}
                    >
                      <TariffLineBands
                        canEdit={state.canEdit}
                        tariffLineUuid={rowData.tariffLineUuid}
                        classes={classes}
                      />
                    </div>
                  );
                },
              },
            ]
          }
          title={`Tariff Details ${tariff.companyName?"for "+tariff.companyName:""}${tariff.currencyCode?` (${tariff.currencyCode})`: `${tariff.tariffType === "DEFAULT" ? ` - ${tariff.description}` : ""}`}`}
          columns={getColumns({canEdit: state.canEdit})}
          data={tariffLines.data}
          editable={{
            isEditable: () => {
              return state.canEdit;
            },
            isDeletable: () => {
              return state.canEdit;
            },
            onRowAdd: state.canEdit
              ? (newData) => new Promise((resolve, reject) => {
                handleAddTariffLine.mutate({record: {priority: 10, ...newData, tariffHeaderUuid: tariff.tariffHeaderUuid} , resolve, reject, extraKeys});
              })
              : undefined,
            onRowUpdate: (newData, oldData) =>
              new Promise((resolve, reject) => {
                handleUpdateTariffLine.mutate({record: newData, oldRecord: oldData, resolve, reject, extraKeys});
              }),
            onRowDelete: (oldData) =>
              new Promise((resolve, reject) => {
                handleDeleteTariffLine.mutate({oldRecord: oldData, resolve, reject, extraKeys});
              }),
          }}
        />
      )}
      </div>
    </div>
  );

}

export default TariffLineList;
